import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface UseTickerScrollOptions {
  /** Скорость автопрокрутки, px/с. */
  pixelsPerSecond: number;
  /** Меняется вместе со списком — тогда ширины перемеряются. */
  itemsKey: unknown;
}

/** Пока палец не оторван и ещё немного после — автопрокрутка стоит, чтобы лента не уезжала из-под руки. */
const TOUCH_RESUME_MS = 2500;
/** Сколько мс без прокрутки считаем «остановились» и возвращаем позицию в рабочую зону. */
const IDLE_NORMALIZE_MS = 180;
/** Сдвиг мыши, начиная с которого жест считается перетаскиванием, а не кликом, px. */
const DRAG_THRESHOLD_PX = 4;
/** Запас у края дорожки, при котором позицию возвращают, не дожидаясь остановки, px. */
const EDGE_GUARD_PX = 120;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Бесконечная лента, которую можно листать рукой.
 *
 * Лента — это обычный горизонтальный скролл (`overflow-x: auto`, полоса скрыта), поэтому тачпад, колесо
 * с Shift, тач и клавиатурный фокус работают сами. Поверх него:
 *  - автопрокрутка по `requestAnimationFrame` (дробная позиция копится отдельно: браузер округляет
 *    `scrollLeft` до пикселя, и 0.8 px за кадр иначе пропадали бы);
 *  - пауза по наведению, фокусу и касанию — временная, лента возобновляется с того места, где остановилась;
 *  - **пауза по кнопке** (`isPaused` / `togglePause`) — постоянная: наведение, фокус и касание её не снимают,
 *    а цикл кадров на ней не крутится (WCAG 2.2.2 «Pause, Stop, Hide»);
 *  - перетаскивание мышью — для тех, у кого нет горизонтального жеста;
 *  - бесшовная петля в обе стороны: копий списка не меньше трёх, рабочая зона — вторая копия
 *    `[L, 2L)`, при выходе за неё позиция сдвигается на ширину списка `L` — картинка при этом та же.
 *
 * Лента **статична** (одна копия, без автопрокрутки, петли и кнопки паузы), когда двигаться нечему:
 *  - `prefers-reduced-motion` — настройка читается с подпиской, смена в ОС применяется сразу;
 *  - весь список помещается в окно (`list <= viewport`): иначе у организации с одним-двумя матчами в ряд
 *    ехали бы одинаковые копии одной записи.
 * Цикл кадров стоит и пока лента вне экрана.
 */
export function useTickerScroll({ pixelsPerSecond, itemsKey }: UseTickerScrollOptions) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [metrics, setMetrics] = useState({ list: 0, viewport: 0 });

  const isReducedMotion = useReducedMotion();
  const [isPaused, setIsPaused] = useState(false);
  const [isOnScreen, setIsOnScreen] = useState(true);

  /** Двигаться нечему: пользователь просит без движения или весь список виден целиком. */
  const isStatic = isReducedMotion || (metrics.list > 0 && metrics.list <= metrics.viewport);

  /** Временная пауза: наведение, фокус, касание. Не путать с `isPaused` — паузой по кнопке. */
  const isHeldRef = useRef(false);
  const lastProgrammaticLeftRef = useRef(Number.NaN);
  const isBusyRef = useRef(false);
  const isFocusWithinRef = useRef(false);
  const touchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const dragRef = useRef<{ startX: number; startScroll: number; isMoved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  const previousListWidthRef = useRef(0);

  const copies = isStatic ? 1 : metrics.list > 0 ? Math.max(3, Math.ceil(metrics.viewport / metrics.list) + 2) : 3;
  /** Копия, доступная скринридеру и табуляции: рабочая зона — вторая. */
  const accessibleCopy = isStatic ? 0 : 1;

  useLayoutEffect(() => {
    const list = listRef.current;
    const viewport = viewportRef.current;
    if (!list || !viewport) return undefined;

    const measure = () => setMetrics({ list: list.getBoundingClientRect().width, viewport: viewport.clientWidth });
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(list);
    observer.observe(viewport);

    return () => observer.disconnect();
  }, [itemsKey, copies]);

  // Рабочая зона начинается со второй копии; при смене ширины списка относительное положение сохраняется.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    if (isStatic) {
      // Двигаться нечему: возвращаем ленту в начало, если до этого она ехала по петле.
      viewport.scrollLeft = 0;
      previousListWidthRef.current = 0;
      return;
    }

    if (metrics.list <= 0) return;

    const previous = previousListWidthRef.current;
    viewport.scrollLeft = previous > 0 ? Math.max(0, viewport.scrollLeft - previous + metrics.list) : metrics.list;
    previousListWidthRef.current = metrics.list;
  }, [metrics.list, isStatic]);

  // Лента вне экрана не крутится.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(([entry]) => setIsOnScreen(entry.isIntersecting));
    observer.observe(viewport);

    return () => observer.disconnect();
  }, []);

  /** Возвращает позицию в рабочую зону `[L, 2L)`; картинка не меняется — копии одинаковы. */
  const normalize = useCallback(() => {
    const viewport = viewportRef.current;
    const width = metrics.list;
    if (!viewport || isStatic || width <= 0 || isFocusWithinRef.current) return;

    const position = viewport.scrollLeft;

    if (position >= 2 * width) viewport.scrollLeft = position - width;
    else if (position < width) viewport.scrollLeft = position + width;
  }, [metrics.list, isStatic]);

  // Автопрокрутка. На паузе по кнопке и вне экрана цикла кадров нет вовсе; на временной паузе (наведение,
  // фокус, касание) он крутится, но ничего не двигает.
  useEffect(() => {
    const viewport = viewportRef.current;
    const width = metrics.list;
    if (!viewport || isStatic || width <= 0 || isPaused || !isOnScreen) return undefined;

    let frame = 0;
    let lastTime = performance.now();
    let position = viewport.scrollLeft;

    const tick = (now: number) => {
      const seconds = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      if (isHeldRef.current || isBusyRef.current || document.hidden) {
        position = viewport.scrollLeft;
      } else {
        // Позицию двигали рукой — продолжаем оттуда, а не с накопленной.
        if (Math.abs(viewport.scrollLeft - position) > 1.5) position = viewport.scrollLeft;

        position += pixelsPerSecond * seconds;
        if (position >= 2 * width) position -= width;
        viewport.scrollLeft = position;
        // Событие `scroll` от этой записи `handleScroll` пропускает: позицию в рабочую зону возвращает сам цикл.
        lastProgrammaticLeftRef.current = viewport.scrollLeft;
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [metrics.list, isStatic, pixelsPerSecond, isPaused, isOnScreen]);

  useEffect(
    () => () => {
      clearTimeout(touchTimerRef.current);
      clearTimeout(idleTimerRef.current);
    },
    [],
  );

  const handleScroll = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || isStatic || metrics.list <= 0 || isFocusWithinRef.current) return;

    // Это отклик на запись самой автопрокрутки, а не на жест: перезапускать от него таймер незачем.
    if (Math.abs(viewport.scrollLeft - lastProgrammaticLeftRef.current) < 0.5) return;

    const maxScroll = viewport.scrollWidth - viewport.clientWidth;

    // У жёсткого края ждать остановки нельзя — упрёмся в конец дорожки.
    if (viewport.scrollLeft < EDGE_GUARD_PX || viewport.scrollLeft > maxScroll - EDGE_GUARD_PX) normalize();

    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(normalize, IDLE_NORMALIZE_MS);
  }, [isStatic, metrics.list, normalize]);

  /** Перетаскивание мышью: пока кнопка зажата, слушаем окно — курсор может уйти за ленту. */
  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const viewport = viewportRef.current;
      if (event.button !== 0 || !viewport) return;

      dragRef.current = { startX: event.clientX, startScroll: viewport.scrollLeft, isMoved: false };

      const handleMove = (moveEvent: MouseEvent) => {
        const drag = dragRef.current;
        if (!drag) return;

        const delta = moveEvent.clientX - drag.startX;
        if (!drag.isMoved && Math.abs(delta) > DRAG_THRESHOLD_PX) {
          drag.isMoved = true;
          isBusyRef.current = true;
        }

        if (drag.isMoved) viewport.scrollLeft = drag.startScroll - delta;
      };

      const handleUp = () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);

        if (dragRef.current?.isMoved) {
          // Отпускание поверх ссылки даёт `click`: после перетаскивания он не нужен.
          suppressClickRef.current = true;
          setTimeout(() => {
            suppressClickRef.current = false;
          }, 0);
          isBusyRef.current = false;
          normalize();
        }

        dragRef.current = null;
      };

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    },
    [normalize],
  );

  const handleClickCapture = useCallback((event: React.MouseEvent) => {
    if (!suppressClickRef.current) return;

    event.preventDefault();
    event.stopPropagation();
  }, []);

  const hold = useCallback(() => {
    isHeldRef.current = true;
  }, []);

  const release = useCallback(() => {
    if (!isFocusWithinRef.current) isHeldRef.current = false;
  }, []);

  /** Кнопка «Остановить / Запустить ленту»: наведение, фокус и касание это состояние не трогают. */
  const togglePause = useCallback(() => setIsPaused((value) => !value), []);

  /** Обработчики корня ленты: наведение, фокус и касание ставят автопрокрутку на временную паузу. */
  const rootHandlers = {
    onMouseEnter: hold,
    onMouseLeave: release,
    onFocus: () => {
      isFocusWithinRef.current = true;
      hold();
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;

      isFocusWithinRef.current = false;
      release();
    },
    onTouchStart: () => {
      clearTimeout(touchTimerRef.current);
      hold();
    },
    onTouchEnd: () => {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = setTimeout(release, TOUCH_RESUME_MS);
    },
  };

  const viewportHandlers = {
    onScroll: handleScroll,
    onMouseDown: handleMouseDown,
    onClickCapture: handleClickCapture,
    // Родное перетаскивание ссылки и картинки перехватило бы жест.
    onDragStart: (event: React.DragEvent) => event.preventDefault(),
  };

  return {
    viewportRef,
    listRef,
    copies,
    accessibleCopy,
    /** Лента движется сама: нужна кнопка паузы. Статичной ленте (reduced motion, всё помещается) она ни к чему. */
    isAnimated: !isStatic,
    isPaused,
    togglePause,
    rootHandlers,
    viewportHandlers,
  };
}

/** `prefers-reduced-motion: reduce` с подпиской: переключатель в настройках ОС применяется без перезагрузки. */
function useReducedMotion(): boolean {
  const [isReduced, setIsReduced] = useState(
    () => typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsReduced(event.matches);
    setIsReduced(query.matches);
    query.addEventListener?.('change', handleChange);

    return () => query.removeEventListener?.('change', handleChange);
  }, []);

  return isReduced;
}
